import './userHistory.scss';
import { useEffect, useState } from 'react';
import dbService from '../../services/database';
import { useNavigate } from 'react-router-dom';
import { FaUser } from 'react-icons/fa';
import Loading from '../Loading/Loading';
import { CSVLink } from 'react-csv';

const UserHistory = ({ userId }) => {
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [userInfo, setUserInfo] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setIsLoading(true);
        console.log('🔄 UserHistory - Fetching data for user:', userId);

        // Fetch user transactions from PostgreSQL
        const userTransactions = await dbService.getUserTransactions(userId);
        console.log(
          '📥 UserHistory - Fetched transactions:',
          userTransactions.length
        );

        // Fetch user dividends (if any exist for this user)
        const allDividends = await dbService.getAllDividends({ limit: 1000 });
        const userDividends = allDividends.data.filter(
          (dividend) => dividend.user_id === userId
        );
        console.log(
          '📥 UserHistory - Fetched dividends:',
          userDividends.length
        );

        // Get user info for current balances and fallback data
        const fetchedUserInfo = await dbService.getUserById(userId);
        setUserInfo(fetchedUserInfo);

        // Transform transactions to unified format
        const transformedTransactions = userTransactions.map((transaction) => {
          const date = new Date(transaction.transaction_date);
          return {
            id: `tx_${transaction.id}`,
            date: date,
            formattedDate: date.toLocaleDateString('en-GB'),
            formattedTime: date.toLocaleTimeString('en-GB', {
              hour12: false,
              hour: '2-digit',
              minute: '2-digit',
            }),
            type: 'Purchase',
            subType: transaction.transaction_type,
            details:
              transaction.description ||
              `Share ${transaction.transaction_type}`,
            amount: parseFloat(transaction.transaction_value) || 0,
            shares: parseFloat(transaction.amount_of_shares) || 0,
            referenceNumber: transaction.reference_number,
            paymentMethod: transaction.payment_method,
            status: transaction.status || 'completed',
            rawTransaction: transaction,
          };
        });

        // Transform dividends to unified format
        const transformedDividends = userDividends.map((dividend) => {
          const date = new Date(dividend.processed_at || dividend.payment_date);
          return {
            id: `div_${dividend.id}`,
            date: date,
            formattedDate: date.toLocaleDateString('en-GB'),
            formattedTime: date.toLocaleTimeString('en-GB', {
              hour12: false,
              hour: '2-digit',
              minute: '2-digit',
            }),
            type: 'Dividend',
            subType: 'payment',
            details: `Dividend Payment for ${dividend.year}`,
            amount: parseFloat(dividend.dividend_amount) || 0,
            shares: 0, // Dividends don't change share count
            shareValue: parseFloat(dividend.share_value) || 0,
            year: dividend.year,
            referenceNumber:
              dividend.payment_reference || `DIV-${dividend.year}`,
            status: dividend.status || 'processed',
            rawDividend: dividend,
          };
        });

        // Combine all transactions and sort by date (most recent first)
        const allTransactions = [
          ...transformedTransactions,
          ...transformedDividends,
        ].sort((a, b) => b.date - a.date);

        // Calculate running balances (working backwards from current balances)
        const currentShares = parseFloat(fetchedUserInfo?.share_amount) || 0;
        const currentValue = parseFloat(fetchedUserInfo?.share_value) || 0;

        let runningShares = currentShares;
        let runningValue = currentValue;

        // Process transactions in reverse order to calculate balances before each transaction
        const transactionsWithBalances = allTransactions.map(
          (transaction, index) => {
            // Balance before this transaction
            const balanceBeforeShares = runningShares;
            const balanceBeforeValue = runningValue;

            // Adjust running balance for next iteration (going backwards in time)
            if (transaction.type === 'Purchase') {
              if (transaction.subType === 'bought') {
                runningShares -= transaction.shares;
                runningValue -= transaction.amount;
              } else {
                runningShares += transaction.shares;
                runningValue += transaction.amount;
              }
            }
            // Dividends don't affect share balance, only value balance

            return {
              ...transaction,
              balanceBeforeShares: Math.max(0, balanceBeforeShares),
              balanceBeforeValue: Math.max(0, balanceBeforeValue),
            };
          }
        );

        console.log('📊 UserHistory - Combined transactions with balances:', {
          total: transactionsWithBalances.length,
          transactions: transformedTransactions.length,
          dividends: transformedDividends.length,
          sample: transactionsWithBalances.slice(0, 2),
        });

        setTransactions(transactionsWithBalances);
        setError(null);
      } catch (err) {
        console.error('❌ UserHistory - Error fetching data:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (userId) {
      fetchHistory();
    }
  }, [userId]);

  const filteredTransactions = transactions.filter((transaction) => {
    if (startDate && transaction.date < new Date(startDate)) {
      return false;
    }
    if (endDate && transaction.date > new Date(endDate)) {
      return false;
    }
    return true;
  });

  const currentTransactions = filteredTransactions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);

  const downloadCSV = () => {
    const csvData = filteredTransactions.map((record) => ({
      Date: record.formattedDate,
      Time: record.formattedTime,
      Type: record.type,
      Details: record.details,
      'Balance B/f (GH₵)': record.balanceBeforeValue?.toFixed(2) || '0.00',
      'Balance B/f (Shares)': record.balanceBeforeShares || 0,
      'Amount Added/Paid (GH₵)': record.amount?.toFixed(2) || '0.00',
      'Shares Added': record.shares || 0,
      Reference: record.referenceNumber || 'N/A',
      Staff:
        record.rawTransaction?.created_by ||
        record.rawDividend?.processed_by ||
        userInfo?.inputter_name ||
        userInfo?.inputter ||
        'System',
      Status: record.status || 'completed',
      Year: record.year || '-',
    }));
    return csvData;
  };

  if (isLoading) {
    return <Loading inline={true} message="Loading user history..." />;
  }

  if (error) {
    return (
      <div className="error-message">Error loading transactions: {error}</div>
    );
  }

  return (
    <div className="user-history">
      <h2>User Transaction History</h2>

      {/* Date Filter Section */}
      <div className="filterSection">
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          placeholder="Start Date"
        />
        <input
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          placeholder="End Date"
        />
        <button
          onClick={() => {
            setStartDate('');
            setEndDate('');
          }}
        >
          Clear Filters
        </button>
      </div>

      {/* Export to CSV */}
      {filteredTransactions.length > 0 && (
        <CSVLink
          data={downloadCSV()}
          filename={`user_transactions_${userId}_${
            new Date().toISOString().split('T')[0]
          }.csv`}
          className="csv-export-btn"
        >
          Export to CSV
        </CSVLink>
      )}

      {filteredTransactions.length > 0 ? (
        <>
          <div className="results-info">
            Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
            {Math.min(currentPage * itemsPerPage, filteredTransactions.length)}{' '}
            of {filteredTransactions.length} transactions
          </div>

          <table className="history-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Type</th>
                <th>Details</th>
                <th>Balance B/f (GH₵)</th>
                <th>Balance B/f (Shares)</th>
                <th>Amount Added/Paid (GH₵)</th>
                <th>Reference</th>
                <th>Staff</th>
                <th>Log</th>
              </tr>
            </thead>
            <tbody>
              {currentTransactions.map((transaction) => (
                <tr key={transaction.id}>
                  <td>{transaction.formattedDate}</td>
                  <td>{transaction.formattedTime}</td>
                  <td>
                    <span
                      className={`type-badge type-${transaction.type.toLowerCase()}`}
                    >
                      {transaction.type}
                    </span>
                  </td>
                  <td>{transaction.details}</td>
                  <td>
                    GH₵ {(transaction.balanceBeforeValue || 0).toFixed(2)}
                  </td>
                  <td>
                    {(transaction.balanceBeforeShares || 0).toLocaleString()}{' '}
                    shares
                  </td>
                  <td>
                    <span
                      className={`amount-${transaction.type.toLowerCase()}`}
                    >
                      GH₵ {transaction.amount.toFixed(2)}
                      {transaction.shares > 0 && (
                        <div className="shares-detail">
                          +{transaction.shares.toLocaleString()} shares
                        </div>
                      )}
                    </span>
                  </td>
                  <td>{transaction.referenceNumber || 'N/A'}</td>
                  <td className="staff-column">
                    <span className="staff-name">
                      {transaction.rawTransaction?.created_by ||
                        transaction.rawDividend?.processed_by ||
                        userInfo?.inputter_name ||
                        userInfo?.inputter ||
                        'System'}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`status-badge status-${transaction.status}`}
                    >
                      {transaction.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="page-btn"
              >
                Previous
              </button>

              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>

              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                Next
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="no-transactions">
          <FaUser className="icon" />
          <h3>No transactions found for this user</h3>
          <p>
            This user hasn't made any transactions yet, or they don't match your
            filters.
          </p>
        </div>
      )}
    </div>
  );
};

export default UserHistory;
